import type React from 'react'
import type { TFunction } from 'react-i18next'

import { RefundFlowState } from '../constants/refundDialogV2Constants'

interface InitiateRefundTransactionDependencies {
  dispatch: React.Dispatch<any>
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  refundAmount: number
  refundInvoiceId: string
  selectedPaymentId: string
  selectedPaymentTerminal: string | null
  setIsLoading: (loading: boolean) => void
  t: TFunction<'Dialogs'>
}

/**
 * Handles the refund transaction mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateRefundTransaction = async (
  deps: InitiateRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    setIsLoading,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentTerminal,
    selectedPaymentId,
    refundInvoiceId,
    t,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId,
          externalDeviceId: selectedPaymentTerminal,
        },
      },
    })

    if (!result?.data?.initiateRefundTransaction || result.errors) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: t(
            'Dialogs:REFUND_DIALOG_V2.FAILED_TO_INITIATE_REFUND_TRANSACTION',
          ),
          severity: 'error',
          status: 500,
        },
      })
    } else {
      dispatch({
        type: 'SET_STATE',
        payload: RefundFlowState.REFUND_PROCESSING,
      })
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: result.data.initiateRefundTransaction.id,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: t(
          'Dialogs:REFUND_DIALOG_V2.FAILED_TO_INITIATE_REFUND_TRANSACTION',
        ),
        severity: 'error',
        status: 500,
      },
    })
  } finally {
    setIsLoading(false)
  }
}
